import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PieChart as PieIcon } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../../utils/api';
import useIsMobile from '../../hooks/useIsMobile';

const STATUS_COLORS = {
  new: '#8b5cf6', open: '#3b82f6', pending: '#f59e0b',
  on_hold: '#a78bfa', resolved: '#10b981', closed: '#6b7280',
};

export const meta = {
  id: 'tickets_by_status',
  category: 'tickets',
  titleKey: 'dashboard.widgets.ticketsByStatus',
  descriptionKey: 'dashboard.widgets.ticketsByStatusDesc',
  defaultSize: 'md',
  icon: PieIcon,
};

// Reads byStatus from ctx (shared /dashboard/stats payload) but owns the
// statusWindow state locally — different agents may want different windows.
export default function TicketsByStatusWidget({ ctx }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [statusWindow, setStatusWindow] = useState('active');

  // If ctx already has byStatus for 'active', use it; otherwise fetch the
  // window the user picked. This keeps the default-view zero-cost.
  const { data: fetched } = useQuery({
    queryKey: ['dashboard-stats-status', statusWindow],
    queryFn: () => api.get('/dashboard/stats', { params: { status_window: statusWindow } }).then(r => r.data),
    enabled: statusWindow !== 'active',
    refetchInterval: 60000,
  });
  const byStatus = (statusWindow === 'active' ? ctx?.byStatus : fetched?.byStatus) || [];

  return (
    <div className="card" style={{ height: '100%' }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="card-title">{t('dashboard.ticketsByStatus')}</div>
        <select
          value={statusWindow}
          onChange={(e) => setStatusWindow(e.target.value)}
          className="form-select"
          style={{ width: 'auto', fontSize: 12, padding: '4px 8px' }}
        >
          <option value="active">{t('dashboard.statusWindow.active')}</option>
          <option value="30d">{t('dashboard.statusWindow.last30d')}</option>
          <option value="all">{t('dashboard.statusWindow.allTime')}</option>
        </select>
      </div>
      <ResponsiveContainer width="100%" height={isMobile ? 160 : 200}>
        <PieChart>
          <Pie
            data={byStatus.map(s => ({ name: t(`tickets.statuses.${s.status}`), value: parseInt(s.count), status: s.status }))}
            cx="50%" cy="50%"
            innerRadius={isMobile ? 40 : 55} outerRadius={isMobile ? 65 : 80}
            dataKey="value"
            label={({ name, value }) => `${name} (${value})`}
            labelLine={false}
            fontSize={11}
            style={{ cursor: 'pointer' }}
            onClick={(payload) => {
              if (payload?.status) navigate(`/tickets?status=${payload.status}`);
            }}
          >
            {byStatus.map((s) => (
              <Cell key={s.status} fill={STATUS_COLORS[s.status] || '#6b7280'} />
            ))}
          </Pie>
          <Tooltip contentStyle={{ background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 8, fontSize: 12 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
